import { setSelectors, addListeners } from './utilities.js';
import { updateCart, removeItemFromCart, getCart } from '@umbrella/cart-service';

class RcQuickCart extends HTMLElement {

  constructor() {
    super();
    this.autoOpen = false;
    this.selectors = {};
    this.events = {};
    this._isFetching = false;
    this._actionsVisibilityObserver = null;
    this._returnFocusTo = null;
    this._cartSignature = null;
  };

  connectedCallback() { this.initialize(); };

  disconnectedCallback() { this.deinitialize(); };

  _withLoading = async (fn) => {
    this.setAttribute('data-loading', 'true');
    try {
      return await fn();
    } finally {
      this.setAttribute('data-loading', 'false');
    };
  };

  _handleCartUpdate = (event) => {
    if(!event || !event.detail) return;
    const { id, key, value } = event.detail;
    const targetId = key || id;
    if(!targetId || typeof value !== 'number') return;
    this._withLoading(async () => {
      await updateCart(targetId, value);
      await this._refreshItems();
    });
  };

  _handleRemoveItem = async (event) => {
    if(!event || !event.target) return;
    const btn = event.target.closest('[data-item-remove]');
    if(!btn || !this.selectors.cartItems || !this.selectors.cartItems.contains(btn)) return;
    event.preventDefault();
    const cartKey = btn.getAttribute('data-item-key');
    if(!cartKey) return;
    await this._withLoading(async () => {
      const result = await removeItemFromCart(cartKey);
      if(result) await this._refreshItems();
    });
  };

  _open = ({refresh = false} = {}) => {
    const wasOpen = this.hasAttribute('open');
    if(!wasOpen) this._returnFocusTo = (document.activeElement instanceof HTMLElement) ? document.activeElement : null;
    this.setAttribute('open', '');
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    document.body.setAttribute('data-overlay-active', 'true');
    if(!wasOpen) {
      document.addEventListener('keydown', this._onKeydown);
      // Move focus into the drawer (the close button) once it is visible.
      if(this.selectors.quickCartClose) requestAnimationFrame(() => this.selectors.quickCartClose.focus());
    }
    if(refresh) this._withLoading(() => this._refreshItems());
    else this._refreshRecommendations();
  };

  _close = () => {
    const wasOpen = this.hasAttribute('open');
    this.removeAttribute('open');
    this.setAttribute('inert', '');
    this.setAttribute('aria-hidden', 'true');
    document.body.removeAttribute('data-overlay-active');
    document.removeEventListener('keydown', this._onKeydown);
    if(wasOpen && this._returnFocusTo && typeof this._returnFocusTo.focus === 'function') this._returnFocusTo.focus();
    this._returnFocusTo = null;
  };

  _onKeydown = (e) => {
    if(e.key === 'Escape') { e.preventDefault(); this._close(); }
    else if(e.key === 'Tab') { this._trapFocus(e); }
  };

  _trapFocus = (e) => {
    const focusables = Array.from(
      this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
    if(!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if(e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if(!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // Stable signature of the cart contents (line key + quantity). Used to ignore
  // `cart:state` syncs that carry no real change (page load, tab focus), which
  // would otherwise reload the drawer and make it visibly blink.
  _signatureFromCart = (cart) => {
    if(!cart || !Array.isArray(cart.items)) return '';
    return cart.items.map((it) => `${it.key}:${it.quantity}`).sort().join('|');
  };

  _signatureFromDom = () => {
    const root = this.selectors.cartItems;
    if(!root) return '';
    const parts = [];
    root.querySelectorAll('[data-item-remove][data-item-key]').forEach((btn) => {
      const key = btn.getAttribute('data-item-key');
      if(!key) return;
      const scope = btn.closest('.qc-row') || root;
      const input = scope.querySelector('input[type="number"]');
      parts.push(`${key}:${input ? input.value || '' : ''}`);
    });
    return parts.sort().join('|');
  };

  _refreshItems = async () => {
    if(this._isFetching || !this.selectors.cartItems) return;
    this._isFetching = true;
    try {
      const html = await getCart('quick-cart-items');
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      // Take the inner markup of the fresh <cart-items> so we don't nest a
      // duplicate <cart-items> inside the live one, and mirror its first-product
      // attribute so recommendations stay in sync with the cart contents.
      const fresh = tmp.querySelector('.qc-items-surface cart-items');
      if(fresh && this.selectors.cartItems) {
        this.selectors.cartItems.innerHTML = fresh.innerHTML;
        const firstProductId = fresh.getAttribute('data-first-product-id');
        if(firstProductId) this.selectors.cartItems.setAttribute('data-first-product-id', firstProductId);
        else this.selectors.cartItems.removeAttribute('data-first-product-id');
      };
      this._updateEmptyState();
    } finally {
      this._isFetching = false;
    };
    // Record the freshly-rendered state so the follow-up `cart:state` sync for
    // this same mutation is recognised as a no-op and doesn't reload again.
    this._cartSignature = this._signatureFromDom();
    await this._refreshRecommendations();
  };

  // Toggle data-cart-empty on the host from the refreshed items markup so the
  // scoped CSS can hide the shipping bar / recs / totals when the cart is empty.
  _updateEmptyState = () => {
    const root = this.selectors.cartItems;
    if(!root) return;
    this.toggleAttribute('data-cart-empty', !!root.querySelector('[data-cart-empty-state]'));
  };

  // Populate the recommendations carousel directly from the recommendations API
  // (seeded by the first cart item). Done here rather than via a lazy web
  // component so it never depends on that component having upgraded yet.
  _refreshRecommendations = async () => {
    const container = this.selectors.recommendations;
    if(!container) return;
    const firstProductId = this.selectors.cartItems && this.selectors.cartItems.getAttribute('data-first-product-id');
    if(!firstProductId) {
      container.innerHTML = '';
      container.classList.add('hidden');
      return;
    };
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}recommendations/products?section_id=quick-cart-items&product_id=${firstProductId}&limit=10&intent=related`);
      if(!response.ok) return;
      const html = await response.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const surface = tmp.querySelector('.qc-recommendations-surface');
      if(surface && surface.querySelector('[data-qc-upsell-card]')) {
        container.innerHTML = surface.innerHTML;
        container.classList.remove('hidden');
      } else {
        container.innerHTML = '';
        container.classList.add('hidden');
      };
    } catch (e) {
      const U = window.umbrellaConfig || {};
      if(U && U.devMode) console.warn('rc-quick-cart: failed to fetch recommendations', e);
    };
  };

  _onCartChange = (event) => {
    if(!this.selectors.cartItems) return;
    // `cart:state` also fires on page load and every tab focus with no real
    // change; only reload when the cart contents actually differ from the drawer.
    const cart = event && event.detail && event.detail.cart;
    if(cart && this._signatureFromCart(cart) === this._cartSignature) return;
    this._withLoading(() => this._refreshItems());
  };

  _setFixedActionsVisible = (isVisible) => {
    if(!this.selectors.fixedActions) return;
    this.selectors.fixedActions.classList.toggle('opacity-0', !isVisible);
    this.selectors.fixedActions.classList.toggle('pointer-events-none', !isVisible);
  };

  _setupActionsVisibilityObserver = () => {
    if(this._actionsVisibilityObserver) {
      this._actionsVisibilityObserver.disconnect();
      this._actionsVisibilityObserver = null;
    }
    if(!this.selectors.scrollSurface || !this.selectors.fixedActions || !this.selectors.originalActions) {
      this._setFixedActionsVisible(true);
      return;
    }
    this._actionsVisibilityObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      this._setFixedActionsVisible(!entry?.isIntersecting);
    }, {
      root: this.selectors.scrollSurface,
      threshold: 0.15
    });
    this._actionsVisibilityObserver.observe(this.selectors.originalActions);
  };

  initialize = () => {
    this.autoOpen = this.getAttribute('data-auto-open') === 'true';
    this.events = window.umbrellaConfig.events;
    addListeners(this, {
      [this.events.quantityIncrement]: this._handleCartUpdate,
      [this.events.quantityDecrement]: this._handleCartUpdate
    });
    this.selectors = setSelectors(this, {
      overlay: '[data-overlay]',
      quickCartClose: '[data-close]',
      cartItems: 'cart-items',
      recommendations: '[data-qc-recommendations]',
      scrollSurface: '.min-h-0.flex-1.overflow-y-auto',
      fixedActions: '[data-qc-fixed-actions]',
      originalActions: '[data-qc-original-actions]'
    });
    // Seed the baseline from the server-rendered markup so the initial
    // `cart:state` sync (and focus syncs) don't trigger a needless reload.
    this._cartSignature = this._signatureFromDom();
    if(this.selectors.overlay) this.selectors.overlay.addEventListener('click', () => this._close());
    if(this.selectors.cartItems) this.selectors.cartItems.addEventListener('click', this._handleRemoveItem);
    if(this.selectors.quickCartClose) this.selectors.quickCartClose.addEventListener('click', () => this._close());
    if(this.events.qcOpen) document.addEventListener(this.events.qcOpen, this._open);
    if(this.events.cartChange) document.addEventListener(this.events.cartChange, this._onCartChange);
    if(this.events.cartAdded && this.autoOpen) document.addEventListener(this.events.cartAdded, () => this._open({refresh: true}));
    this._setupActionsVisibilityObserver();
  };

  deinitialize = () => {
    // Ensure global overlay state doesn't get stuck if this element is removed while open.
    this._close();
    if (this.selectors.overlay) this.selectors.overlay.removeEventListener('click', () => this._close());
    if(this.selectors.quickCartClose) this.selectors.quickCartClose.removeEventListener('click', () => this._close());
    if(this.selectors.cartItems) this.selectors.cartItems.removeEventListener('click', this._handleRemoveItem);
    if(this.events && this.events.qcOpen) document.removeEventListener(this.events.qcOpen, this._open);
    if(this.events && this.events.cartChange) document.removeEventListener(this.events.cartChange, this._onCartChange);
    if(this.events && this.events.cartAdded) document.removeEventListener(this.events.cartAdded, () => this._open());
    if(this._actionsVisibilityObserver) this._actionsVisibilityObserver.disconnect();
  };
};

if(!customElements.get('rc-quick-cart')) customElements.define('rc-quick-cart', RcQuickCart);